import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Eye, Check } from 'lucide-react';
import { Product } from '../types';
import { formatPrice } from '../utils/helpers';
import Button from './ui/Button';
import { useCartStore } from '../store/cartStore';
import { useAddToCartAnimation } from '../hooks/useAddToCartAnimation';

interface ProductCardProps {
  product: Product;
  onViewDetails: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onViewDetails }) => {
  const addItem = useCartStore((state) => state.addItem);
  const { isAnimating, triggerAnimation } = useAddToCartAnimation();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addItem(product);
    triggerAnimation();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="card group"
    >
      <div className="relative overflow-hidden">
        <img
          src={product.images[0]}
          alt={product.title}
          className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {product.featured && (
          <div className="absolute top-2 left-2 bg-primary-600 text-white px-2 py-1 rounded-full text-xs font-medium">
            Featured
          </div>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddToCart}
              className="bg-white border-white text-primary-600 hover:bg-primary-50"
            >
              {isAnimating ? (
                <Check className="w-4 h-4 mr-1 text-green-600" />
              ) : (
                <ShoppingCart className="w-4 h-4 mr-1" />
              )}
              {isAnimating ? 'Added!' : 'Add to Cart'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(product)}
              className="bg-white border-white text-primary-600 hover:bg-primary-50"
            >
              <Eye className="w-4 h-4 mr-1" />
              View
            </Button>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors">
          {product.title}
        </h3>
        <p className="text-secondary-600 text-sm mb-4 line-clamp-2">
          {product.description}
        </p>
        
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-primary-600">
            {formatPrice(product.price)}
          </span>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(product)}
            >
              <Eye className="w-4 h-4 mr-1" />
              Details
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddToCart}
            >
              {isAnimating ? (
                <Check className="w-4 h-4 mr-1 text-green-600" />
              ) : (
                <ShoppingCart className="w-4 h-4 mr-1" />
              )}
              {isAnimating ? 'Added!' : 'Add'}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard; 